"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertInfoOutline18, IconArrowTrendUpOutline18, IconCircleCheckOutline18, IconClockOutline18, IconCode2Outline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAddressValidation } from "@/hooks/useAddressValidation";
import { useChainInfo } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { useContractABI } from "@/hooks/useContractABI";
import { useTrendingContracts } from "@/hooks/useTrendingContracts";
import { formatAddress } from "@/lib/utils";
import { toChecksumAddress } from "@/lib/utils/address";
import { extractWorkflowContractAddresses } from "@/lib/utils/extractWorkflowAddresses";
import { addRecentContract, getRecentContracts } from "@/lib/utils/recentSelections";
import type { Node } from "@xyflow/react";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Badge } from "@/components/ui/badge";
import Avatar from "boring-avatars";
import { avatarColors } from "@/config/constants";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAddress, isAddress } from "viem";
interface ContractAddressInputProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  chainId?: number;
  onABIFetched?: (abi: string) => void;
  allNodes?: Node[];
  popoverModal?: boolean;
}
function ContractIcon(t0) {
  const $ = _c(11);
  const {
    address,
    size: t1
  } = t0;
  const size = t1 === undefined ? 36 : t1;
  let t2;
  if ($[0] !== size) {
    t2 = {
      width: size,
      height: size
    };
    $[0] = size;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== address) {
    t3 = address.toLowerCase();
    $[2] = address;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== size || $[5] !== t3) {
    t4 = <Avatar size={size} name={t3} variant="pixel" colors={avatarColors} />;
    $[4] = size;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute inset-0 flex items-center justify-center bg-black/20"><IconCode2Outline18 className="h-3.5 w-3.5 text-white drop-shadow" /></div>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t4) {
    t6 = <div className="relative flex flex-shrink-0 items-center justify-center overflow-hidden rounded-xl" style={t2}>{t4}{t5}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function ChainBadge(t0) {
  const $ = _c(8);
  const {
    chainId
  } = t0;
  const {
    data: chainInfo
  } = useChainInfo(chainId);
  if (!chainInfo) {
    return null;
  }
  let t1;
  if ($[0] !== chainInfo.iconUrl || $[1] !== chainInfo.name) {
    t1 = chainInfo.iconUrl && <img src={chainInfo.iconUrl} alt={chainInfo.name} className="h-3.5 w-3.5 rounded-full" />;
    $[0] = chainInfo.iconUrl;
    $[1] = chainInfo.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== chainInfo.name) {
    t2 = <span className="text-ds-text-subtle text-xs">{chainInfo.name}</span>;
    $[3] = chainInfo.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex items-center gap-1">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function SuggestionRow(t0) {
  const $ = _c(9);
  const {
    address,
    name,
    onClick
  } = t0;
  let t1;
  if ($[0] !== address) {
    t1 = <ContractIcon address={address} size={28} />;
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address || $[3] !== name) {
    t2 = <div className="min-w-0 flex-1">{name ? <><p className="text-ds-text-primary truncate text-xs font-medium">{name}</p><p className="text-ds-text-subtle dark:text-ds-text-disabled truncate font-mono text-xs">{formatAddress(address)}</p></> : <p className="text-ds-text-primary truncate font-mono text-xs font-medium">{formatAddress(address)}</p>}</div>;
    $[2] = address;
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onClick || $[6] !== t1 || $[7] !== t2) {
    t3 = <button onClick={onClick} className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors hover:bg-purple-50 dark:hover:bg-purple-950/20">{t1}{t2}</button>;
    $[5] = onClick;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function ContractAddressInput({
  label,
  description,
  headerRight,
  value,
  onChange,
  placeholder = "0x...",
  required = false,
  chainId,
  onABIFetched,
  allNodes = [],
  popoverModal = false
}: ContractAddressInputProps) {
  const {
    isValid,
    isTouched,
    showError,
    setTouched
  } = useAddressValidation(value);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentContracts, setRecentContracts] = useState(getRecentContracts());
  const [isEditing, setIsEditing] = useState(false);
  const {
    data: chainInfo
  } = useChainInfo(chainId);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    contracts: trendingContracts,
    isLoading: loadingTrending
  } = useTrendingContracts({
    chainId,
    limit: 15,
    enabled: !!chainId
  });
  const usedAddresses = useMemo(() => extractWorkflowContractAddresses(allNodes), [allNodes]);
  const {
    events,
    isLoading: isFetchingABI,
    status: fetchStatus,
    error,
    contractName,
    nameTag,
    isProxy,
    proxyContractName,
    implementationAddress,
    warning
  } = useContractABI({
    chainId,
    address: value,
    enabled: isValid && !!value && !!chainId
  });
  const suggestions = useMemo(() => {
    const recentForChain = recentContracts.filter(c => c.chainId === chainId && c.address.toLowerCase() !== value.toLowerCase()).map(c_0 => ({
      address: toChecksumAddress(c_0.address),
      name: c_0.name || null,
      type: "recent" as const
    }));
    const usedSuggestions = usedAddresses.filter(addr => addr !== value.toLowerCase() && !recentContracts.some(r => r.address.toLowerCase() === addr)).map(addr_0 => {
      const trendingMatch = trendingContracts.find(c_1 => c_1.address.toLowerCase() === addr_0);
      return {
        address: toChecksumAddress(addr_0),
        name: trendingMatch?.name || trendingMatch?.nameTag || null,
        type: "used" as const
      };
    });
    const trendingSuggestions = trendingContracts.filter(contract => !usedAddresses.includes(contract.address.toLowerCase()) && !recentContracts.some(r_0 => r_0.address.toLowerCase() === contract.address.toLowerCase())).map(contract_0 => ({
      address: toChecksumAddress(contract_0.address),
      name: contract_0.name || contract_0.nameTag,
      type: "trending" as const
    }));
    return [...recentForChain, ...usedSuggestions, ...trendingSuggestions];
  }, [recentContracts, chainId, usedAddresses, trendingContracts, value]);
  const filteredSuggestions = useMemo(() => {
    if (!searchQuery) return suggestions;
    const query = searchQuery.toLowerCase();
    return suggestions.filter(s => s.address.toLowerCase().includes(query) || s.name && s.name.toLowerCase().includes(query));
  }, [suggestions, searchQuery]);
  useEffect(() => {
    if (fetchStatus === "success" && events.length > 0 && onABIFetched) {
      const abiString = JSON.stringify(events, null, 2);
      console.log(`[ContractAddressInput] Calling onABIFetched with ${events.length} events:`, events.map(e => e.name));
      onABIFetched(abiString);
    }
  }, [fetchStatus, events, onABIFetched]);
  const handleSelectSuggestion = (address: string, contractName_0?: string) => {
    let checksumAddress = address;
    if (isAddress(address)) {
      try {
        checksumAddress = getAddress(address);
      } catch {
        checksumAddress = address;
      }
    }
    onChange(checksumAddress);
    setOpen(false);
    setSearchQuery("");
    setIsEditing(false);
    if (chainId) {
      addRecentContract({
        address: checksumAddress,
        chainId,
        name: contractName_0
      });
      setRecentContracts(getRecentContracts());
    }
  };
  const handleInputChange = (newValue: string) => {
    let formattedValue = newValue;
    if (isAddress(newValue)) {
      try {
        formattedValue = getAddress(newValue);
      } catch {
        formattedValue = newValue;
      }
    }
    onChange(formattedValue);
    setSearchQuery(formattedValue);
    if (!isAddress(formattedValue) && suggestions.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };
  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (!(value && isAddress(value)) && suggestions.length > 0) {
      setTimeout(() => setOpen(true), 0);
    }
  };
  const handleEditClick = () => {
    setIsEditing(true);
  };
  const handleBlur = useCallback(() => {
    setTouched(true);
    blurTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      setIsEditing(false);
      blurTimeoutRef.current = null;
    }, 200);
  }, [setTouched]);
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);
  const fetchMessage = fetchStatus === "success" && events.length > 0 ? `` : fetchStatus === "success" && events.length === 0 ? "No events found in contract ABI. You can manually enter the event ABI below." : fetchStatus === "not-found" ? error || "Contract not verified on any explorer. You can manually enter the event ABI below." : fetchStatus === "error" ? "Failed to fetch ABI. You can manually enter the event ABI below." : "";
  const displayName = nameTag || contractName;
  const hasValidSelection = isValid && !!value && !isEditing;
  const hasContractInfo = hasValidSelection && (displayName || fetchStatus === "success");
  const isVerified = fetchStatus === "success" && !!displayName;
  return <div className="space-y-2">
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      {hasContractInfo && !isFetchingABI ? <div className="bg-ds-surface-card dark:hover:bg-ds-neutral-700 hover:bg-ds-surface-hover flex w-full cursor-pointer items-center gap-3 rounded-xl border border-green-500 px-3 py-2.5 text-sm transition-colors" onClick={handleEditClick}>
          <ContractIcon address={value} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {displayName && <span className="text-ds-text-primary truncate font-medium">{displayName}</span>}
              {isVerified && <Badge variant="outline" className="label-2xs gap-0.5 border-green-200 px-1.5 text-green-600 dark:border-green-800 dark:text-green-400">
                  <IconCircleCheckOutline18 className="h-2.5 w-2.5" />
                  Verified
                </Badge>}
              {!isVerified && fetchStatus === "not-found" && <Badge variant="outline" className="label-2xs gap-0.5 border-yellow-200 px-1.5 text-yellow-600 dark:border-yellow-800 dark:text-yellow-400">
                  <IconAlertInfoOutline18 className="h-2.5 w-2.5" />
                  Unverified
                </Badge>}
            </div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-ds-text-subtle dark:text-ds-text-disabled font-mono text-xs">
                {formatAddress(value)}
              </span>
              {isProxy && proxyContractName && <span className="text-ds-text-muted text-xs">Proxy &rarr; {proxyContractName}</span>}
              {chainId && <ChainBadge chainId={chainId} />}
            </div>
            {warning && <p className="mt-0.5 text-xs text-yellow-600 dark:text-yellow-400">{warning}</p>}
          </div>
          <IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" />
        </div> : <Popover open={open} onOpenChange={newOpen => {
      if (newOpen || suggestions.length === 0) {
        setOpen(newOpen);
      }
    }} modal={popoverModal}>
          <PopoverTrigger asChild>
            <div className="relative">
              <Input type="text" value={value || ""} onChange={e_0 => handleInputChange(e_0.target.value)} onFocus={handleFocus} onBlur={handleBlur} placeholder={placeholder} autoFocus={isEditing} className={`pr-9 font-mono ${showError || isTouched && !value && required ? "border-red-500 focus-visible:ring-red-500" : fetchStatus === "success" ? "border-green-500 focus-visible:ring-green-500" : ""}`} />

              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                {isFetchingABI && <PixelLoader size="sm" color="blue" />}
                {!isFetchingABI && showError && <IconAlertInfoOutline18 className="h-4 w-4 text-red-500" />}
                {!isFetchingABI && isTouched && value && isValid && fetchStatus === "idle" && <IconCircleCheckOutline18 className="h-4 w-4 text-green-500" />}
                {!isFetchingABI && fetchStatus === "success" && <IconCircleCheckOutline18 className="h-4 w-4 text-green-500" />}
              </div>
            </div>
          </PopoverTrigger>

          {suggestions.length > 0 && <PopoverContent className="border-ds-border-default bg-ds-surface-card w-[var(--radix-popover-trigger-width)] p-0 shadow-xl" align="start" onOpenAutoFocus={e_1 => e_1.preventDefault()}>
              <div className="max-h-[300px] overflow-y-auto">
                {loadingTrending && filteredSuggestions.length === 0 ? <div className="label-xs flex items-center justify-center py-4">
                    <PixelLoader size="xs" className="mr-2" />
                    Loading suggestions...
                  </div> : filteredSuggestions.length === 0 ? <div className="label-xs py-4 text-center">No suggestions found</div> : <div className="space-y-1">
                    {filteredSuggestions.some(s_1 => s_1.type === "recent") && <div>
                        <div className="dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2">
                          <IconClockOutline18 className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" />
                          <p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">
                            Recently Used
                          </p>
                        </div>
                        <div className="space-y-0.5 py-0.5">
                          {filteredSuggestions.filter(s_0 => s_0.type === "recent").map((suggestion, idx) => <SuggestionRow key={`recent-${idx}`} address={suggestion.address} name={suggestion.name} onClick={() => handleSelectSuggestion(suggestion.address, suggestion.name || undefined)} />)}
                        </div>
                      </div>}

                    {filteredSuggestions.some(s_3 => s_3.type === "used") && <div>
                        <div className="dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2">
                          <IconClockOutline18 className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" />
                          <p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">
                            From your workflow
                          </p>
                        </div>
                        <div className="space-y-0.5 py-0.5">
                          {filteredSuggestions.filter(s_2 => s_2.type === "used").map((suggestion_0, idx_0) => <SuggestionRow key={`used-${idx_0}`} address={suggestion_0.address} name={suggestion_0.name} onClick={() => handleSelectSuggestion(suggestion_0.address, suggestion_0.name || undefined)} />)}
                        </div>
                      </div>}

                    {filteredSuggestions.some(s_6 => s_6.type === "trending") && <div>
                        {filteredSuggestions.some(s_4 => s_4.type === "used") && <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-1 h-px" />}
                        <div className="dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2">
                          <IconArrowTrendUpOutline18 className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" />
                          <p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">
                            Trending on {chainInfo?.name || (chainId ? `Chain ${chainId}` : "EVM")}
                          </p>
                        </div>
                        <div className="space-y-0.5 py-0.5">
                          {filteredSuggestions.filter(s_5 => s_5.type === "trending").map((suggestion_1, idx_1) => <SuggestionRow key={`trending-${idx_1}`} address={suggestion_1.address} name={suggestion_1.name} onClick={() => handleSelectSuggestion(suggestion_1.address, suggestion_1.name || undefined)} />)}
                        </div>
                      </div>}
                  </div>}
              </div>
            </PopoverContent>}
        </Popover>}

      {isFetchingABI && <p className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
          <PixelLoader size="xs" /> Fetching contract ABI...
        </p>}
      {!hasContractInfo && fetchStatus === "not-found" && fetchMessage && <p className="text-xs text-yellow-600 dark:text-yellow-400">{fetchMessage}</p>}
      {!hasContractInfo && fetchStatus === "error" && fetchMessage && <p className="text-xs text-blue-600 dark:text-blue-400">{fetchMessage}</p>}
      {!hasContractInfo && fetchStatus === "success" && fetchMessage && <p className="text-xs text-green-600 dark:text-green-400">{fetchMessage}</p>}
      {showError && <p className="micro-label text-red-600 dark:text-red-400">Please enter a valid contract address</p>}
      {isTouched && !value && required && <p className="micro-label text-red-600 dark:text-red-400">Address is required</p>}
      {!value && !required && fetchStatus === "idle" && <p className="meta-xs">Optional - Leave empty to match events from all contracts</p>}
      {!value && required && <p className="meta-xs">Enter a contract address</p>}
    </div>;
}
export default memo(ContractAddressInput);